import { OtpError } from '@0x234/common-vue/errors/otp-error';
import { RequestManager } from '@jaseeey/request-manager';
import { useMessagePack } from 'composables/message-pack';
import { useOtpManager } from 'composables/otp-manager';
import { strategyProperties } from 'configs/strategies';
import { defineStore } from 'pinia';
import { usePiniaManager } from 'src/composables/pinia-manager';
import { useUserSearchPreferencesStore } from 'stores/user-search-preferences-store';
import { computed, ref } from 'vue';

export const useAuthStore = defineStore('auth', () => {

    const { decodeResponse } = useMessagePack();
    const otpManager = useOtpManager();
    const piniaManager = usePiniaManager();

    const accounts = ref([]);
    const _accountsDirty = ref(false);
    const _accountsLoadedAt = ref(null);

    const strategies = ref([]);
    const _strategiesDirty = ref(false);
    const _strategiesLoadedAt = ref(null);

    const isStrategiesLoaded = computed(() => !!_strategiesLoadedAt.value);

    const user = ref();
    const _userDirty = ref(false);
    const _userLoadedAt = ref();

    const isUserAuthenticated = computed(() => !!user.value);
    const isUserSharedAcrossSites = computed(() => user.value?.sites?.length > 1);

    const linkedStrategies = computed(() => {
        return [{
            id: -1,
            providerId: 'email',
            providerUsername: user.value.email,
            locked: true
        }, ...(accounts.value || [])];
    });

    const availableStrategies = computed(() => {
        const activeStrategies = linkedStrategies.value.map(a => a.providerId);
        return Array.from(strategies.value.values()).filter(p => !activeStrategies.includes(p.id));
    });

    const strategyById = computed(() => {
        const strategyMap = new Map(strategies.value.map(s => [s.id, s]));
        return providerId => strategyMap.get(providerId) || null;
    });

    async function requestLoginOtp(email, captcha = undefined) {
        try {
            return (await this.$auth.postWithCaptcha('/login', { email }, { captcha })).data.token;
        } catch (err) {
            _handleOtpError(err);
        }
    }

    async function reissueOtp(authJwt) {
        const headers = { authorization: 'Bearer ' + authJwt };
        try {
            return (await this.$auth.post('/reissue-token', {}, { headers })).data.token;
        } catch (err) {
            _handleOtpError(err);
        }
    }

    async function verifyOtp(authJwt, otpToken, email) {
        const data = { email, token: otpToken.toString() };
        const headers = { authorization: 'Bearer ' + authJwt };
        try {
            await this.$auth.post('/user/verify', data, { headers });
            invalidateUser.bind(this)();
            await loadUser.bind(this)();
        } catch (err) {
            _handleOtpError(err);
        }
    }

    function _handleOtpError(err) {
        if (err.response && err.response.data.name === 'RequestLimitExceededError') {
            const retryAfter = err.response.headers['retry-after']
                ? parseInt(err.response.headers['retry-after'], 10)
                : null;
            throw new OtpError('Too many attempts, please wait a moment and try again.', {
                originalError: err,
                retryAfter
            });
        }
        if (err.response && err.response.data.name === 'TokenMismatchError') {
            throw new OtpError('Invalid code, please try again or request a new code.', {
                originalError: err
            });
        }
        throw err;
    }

    async function loadAccounts() {
        if (!piniaManager.isInvalidated(_accountsDirty, _accountsLoadedAt)) {
            return;
        }
        await RequestManager.call(this.$auth, 'get', '/accounts', {}, {}, res => {
            if (!res.data || !Array.isArray(res.data)) {
                return;
            }
            accounts.value = res.data;
            _accountsDirty.value = false;
            _accountsLoadedAt.value = Date.now();
        });
    }

    async function deleteAccount(accountId) {
        await this.$auth.delete(`/user/accounts/${accountId}`);
        const accountIndex = accounts.value.findIndex(a => a.id === accountId);
        if (accountIndex !== -1) accounts.value.splice(accountIndex, 1);
    }

    async function loadStrategies() {
        if (!piniaManager.isInvalidated(_strategiesDirty, _strategiesLoadedAt)) {
            return;
        }
        await RequestManager.call(this.$auth, 'get', '/strategies', {}, {}, res => {
            if (!res.data || !Array.isArray(res.data)) {
                return;
            }
            strategies.value = [];
            for (const s of res.data) {
                strategies.value.push({ ...strategyProperties[s.id], ...s });
            }
            strategies.value.sort((a, b) => {
                if (a.order < b.order) return -1;
                if (a.order > b.order) return 1;
                return 0;
            });
            _strategiesLoadedAt.value = Date.now();
        });
    }

    function hasLoadedStrategy(providerId) {
        for (const s of strategies.value) {
            if (s.id === providerId) return true;
        }
        return false;
    }

    function invalidateUser() {
        _userDirty.value = true;
    }

    function setUser(updatedUser) {
        const { accounts: accountsResource, searchPreferences: searchPreferencesResource, ...remainingUserAttrs } = updatedUser;
        accounts.value = accountsResource;
        _accountsDirty.value = false;
        _accountsLoadedAt.value = Date.now();
        user.value = remainingUserAttrs;
        _userDirty.value = false;
        _userLoadedAt.value = Date.now();
        // Store search preferences in the user search preferences store.
        const userSearchPreferencesStore = useUserSearchPreferencesStore();
        userSearchPreferencesStore.preferences = searchPreferencesResource;
        userSearchPreferencesStore.preferencesLoadedAt = Date.now();

    }

    function clearUser() {
        accounts.value = null;
        _accountsDirty.value = false;
        _accountsLoadedAt.value = null;
        user.value = null;
        _userDirty.value = false;
        _userLoadedAt.value = null;
    }

    async function loadUser() {
        if (!piniaManager.isInvalidated(_userDirty, _userLoadedAt)) {
            return;
        }
        await RequestManager.call(this.$auth, 'get', '/user', {}, {
            responseType: 'arraybuffer',
            headers: {
                'Accept': 'application/json,application/msgpack'
            }
        }, res => {
            const decodedData = decodeResponse(res);
            setUser.bind(this)(decodedData);
        }, () => {
            clearUser.bind(this)();
            _userLoadedAt.value = Date.now();
        });
    }

    async function saveUser(updatedUser) {
        const res = await this.$auth.put('/user', updatedUser);
        if (res.status === 202) {
            otpManager.openOtpDialog(res.data.token, updatedUser.email).onOk(async () => {
                // Reload user on successful verification.
                invalidateUser.bind(this)();
                await loadUser.bind(this)();
            });
            return;
        }
        user.value = res.data;
    }

    async function logoutUser() {
        await this.$auth.get('/logout');
        piniaManager.emitSessionTerminated.bind(this)();
    }

    piniaManager.onSessionTerminated(() => {
        clearUser.bind(this)();
    });

    return {
        accounts,
        _accountsDirty,
        _accountsLoadedAt,
        strategies,
        isStrategiesLoaded,
        user,
        isUserAuthenticated,
        isUserSharedAcrossSites,
        linkedStrategies,
        availableStrategies,
        strategyById,
        requestLoginOtp,
        reissueOtp,
        verifyOtp,
        loadAccounts,
        deleteAccount,
        loadStrategies,
        hasLoadedStrategy,
        invalidateUser,
        loadUser,
        saveUser,
        logoutUser
    };
});
